// components/Login.js - Complete with authentication events
import React, { useState, useCallback, useEffect } from "react";
import { login, loginWithGoogle } from "store/auth/thunks";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage, useModal } from "context";
import { useConsent } from "context/ConsentContext";
import { clearError } from "store/auth/authSlice";
import { InputWithIcon } from "reusableComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAuthError,
  selectAuthLoading,
  selectIsAuthenticated,
  selectCurrentUser,
  selectIsAdmin,
  selectIsImpersonated,
} from "store/auth/selectors";
import { emailIcon, passwordIcon } from "../../assets/icons";
import ForgotPasswordForm from "modals/ForgotPasswordForm";
import SignupForm from "../SignupForm";
import styles from "./styles.module.scss";

// Helper to track authentication events (ALWAYS fires, no consent needed)
const trackAuthEvent = (eventName, eventData = {}) => {
  try {
    if (typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];

    // Determine the clean label based on method
    let cleanLabel = "Se connecter";
    if (eventData.method === "google") {
      cleanLabel = "Google Login";
    } else if (eventData.method === "email") {
      cleanLabel = "Se connecter";
    }

    const authEvent = {
      event: eventName,
      // ✅ STATIC label (THIS FIXES THE CONCATENATION ISSUE)
      // The label NEVER changes — it's hardcoded, not read from DOM
      event_label: cleanLabel,
      event_type: "authentication_required",
      legal_basis: "GDPR Article 6(1)(b) - contract performance and security",
      purpose: "authentication_and_security_monitoring",
      attributes: {
        route: window.location.pathname || "/",
        user_type: eventData.user_type || "regular",
        login_method: eventData.method || "form",
        timestamp: new Date().toISOString(),
        session_id: getOrCreateSessionId(),
        is_impersonation: eventData.is_impersonation || false,
        for_booking_form: eventData.for_booking_form || false,
        status: eventData.status || "attempted",
      },
    };

    // Add email hash if provided (for security correlation without PII)
    if (eventData.email) {
      authEvent.attributes.email_hash = hashEmail(eventData.email);
    }

    // Add reason for failures
    if (eventData.reason) {
      authEvent.attributes.failure_reason = eventData.reason;
    }

    window.dataLayer.push(authEvent);

    if (process.env.NODE_ENV === "development") {
      console.log(`[Auth] ${eventName} tracked:`, eventData);
    }
  } catch (error) {
    console.error("[Auth] Failed to track auth event:", error);
  }
};

// Helper: Get or create session ID (stored in sessionStorage only)
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem("gtm_functional_session_id");
  if (!sessionId) {
    sessionId =
      "func_sess_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem("gtm_functional_session_id", sessionId);
  }
  return sessionId;
};

// Helper: Hash email for security tracking without storing PII
const hashEmail = (email) => {
  if (!email) return null;
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
};

const Login = ({
  onSuccess,
  onClose,
  forBookingForm = false,
  spaceId = null,
  returnUrl = null,
  delegateRedirectToCaller = false, // ← NEW: opt-in flag, default false
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [t] = useTranslation("global");
  const { closeModal, openModal } = useModal();
  const { hasConsent } = useConsent();
  const isInModal = !!closeModal;

  // Selectors
  const error = useSelector(selectAuthError);
  const loading = useSelector(selectAuthLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectIsAdmin);
  const isImpersonated = useSelector(selectIsImpersonated);

  // Local state
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [loginInProgress, setLoginInProgress] = useState(false);

  // Get URL parameters
  const targetUid = searchParams.get("uid");
  const rt = searchParams.get("rt");
  const rtp = searchParams.get("rtp");
  const code = searchParams.get("code"); // Google OAuth code

  // Check what type of login this is
  const isAdminImpersonation = !!targetUid && !rt && !rtp;
  const isAdminPreviewFlow = !!targetUid && rt === "app.space" && !!rtp;

  useEffect(() => {
    if (!delegateRedirectToCaller) {
      sessionStorage.removeItem("post_login_return_url");
    }
  }, [delegateRedirectToCaller]);

  // Store booking form context for Google OAuth redirect
  useEffect(() => {
    if (forBookingForm && spaceId) {
      sessionStorage.setItem(
        "booking_form_context",
        JSON.stringify({
          spaceId,
          returnUrl: returnUrl || `/space/${spaceId}`,
          timestamp: Date.now(),
          forBookingForm: true,
        }),
      );
    }
  }, [forBookingForm, spaceId, returnUrl]);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      if (code && !isAuthenticated) {
        setLoginInProgress(true);

        // Track Google login attempt
        trackAuthEvent("validation-formulaire-connexion", {
          method: "google",
          user_type: targetUid ? "admin_impersonation" : "regular",
          email: null,
          for_booking_form: forBookingForm,
          status: "attempted",
        });

        try {
          // Check if this is from booking form
          const bookingContext = sessionStorage.getItem("booking_form_context");

          console.log("Google callback - forBookingForm:", forBookingForm);
          console.log("Google callback - bookingContext:", bookingContext);

          const action = await dispatch(
            loginWithGoogle({
              code,
              language: language.code,
              forBookingForm: forBookingForm,
              spaceId: spaceId,
            }),
          );

          if (loginWithGoogle.fulfilled.match(action)) {
            // Track successful Google login
            trackAuthEvent("validation-formulaire-connexion", {
              method: "google",
              user_type: targetUid ? "admin_impersonation" : "regular",
              email: action.payload?.user?.email,
              for_booking_form: forBookingForm,
              status: "success",
            });

            // Clear Google OAuth state
            sessionStorage.removeItem("google_oauth_state");
            sessionStorage.removeItem("google_oauth_intent");

            // Close the modal if it's open
            if (isInModal) {
              closeModal();
            }

            // IMPORTANT: If this is a booking form, call onSuccess callback
            if (forBookingForm && onSuccess) {
              console.log(
                "Google login: Calling onSuccess callback for booking form",
              );
              onSuccess();
              return;
            }

            // If we have booking context, use it
            if (bookingContext) {
              const context = JSON.parse(bookingContext);
              sessionStorage.removeItem("booking_form_context");

              if (context.returnUrl) {
                console.log(
                  "Google login: Redirecting to returnUrl:",
                  context.returnUrl,
                );
                navigate(context.returnUrl, { replace: true });
                return;
              }
            }

            // Regular login flow
            const returnRT = sessionStorage.getItem("loginReturnRT");
            const returnRTP = sessionStorage.getItem("loginReturnRTP");

            if (returnRT === "app.space" && returnRTP) {
              try {
                const returnParams = JSON.parse(decodeURIComponent(returnRTP));
                if (returnParams.spaceID) {
                  navigate(`/space/${returnParams.spaceID}`, { replace: true });
                  return;
                }
              } catch (error) {
                console.error("[Login] Failed to parse return params:", error);
              }
            }

            handleLoginSuccess(action, false, true);
          } else if (loginWithGoogle.rejected.match(action)) {
            // Track failed Google login
           
            
            showErrorModal(
              action.payload?.[0]?.message ||
                t(
                  "modal.login.googleLoginFailed",
                  "Google login failed. Please try again.",
                ),
            );
          }
        } catch (error) {
          // Track error Google login
          trackAuthEvent("validation-formulaire-connexion", {
            method: "google",
            user_type: targetUid ? "admin_impersonation" : "regular",
            email: null,
            for_booking_form: forBookingForm,
            status: "error",
            reason: error.message,
          });
          console.error("Google login error:", error);
          showErrorModal(
            t(
              "modal.login.googleLoginError",
              "An unexpected error occurred during Google login.",
            ),
          );
        } finally {
          setLoginInProgress(false);
        }
      }
    };

    handleGoogleCallback();
  }, [
    code,
    dispatch,
    language.code,
    isAuthenticated,
    forBookingForm,
    onSuccess,
    navigate,
    t,
    isInModal,
    closeModal,
    spaceId,
  ]);

  // Get redirect URI for Google OAuth
  const getRedirectUri = useCallback(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/auth/auth-endpoint`;
  }, []);

  // Handle Google login button click
  const handleGoogleLoginClick = () => {
    if (loginInProgress) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = getRedirectUri();

    if (!clientId) {
      showErrorModal(
        t("modal.login.googleConfigError", "Google login configuration error"),
      );
      return;
    }

    // ============================================================
    // 🔥 CRITICAL FIX: Store the return URL before starting OAuth
    // This tells AuthEndpoint where to redirect after login
    // ============================================================
    const currentUrl = window.location.pathname + window.location.search;
    sessionStorage.setItem("oauth_return_url", currentUrl);
    console.log("[Login] Stored oauth_return_url:", currentUrl);

    // Store intent and context for callback
    sessionStorage.setItem(
      "google_oauth_intent",
      forBookingForm ? "booking_login" : "login",
    );

    // Store booking form context for redirect after OAuth
    if (forBookingForm) {
      const currentReturnUrl =
        returnUrl || (spaceId ? `/space/${spaceId}` : window.location.pathname);
      sessionStorage.setItem(
        "booking_form_context",
        JSON.stringify({
          spaceId: spaceId,
          returnUrl: currentReturnUrl,
          timestamp: Date.now(),
          forBookingForm: true,
        }),
      );
      console.log("Stored booking context for Google login:", {
        spaceId,
        returnUrl: currentReturnUrl,
      });
    }

    const state = JSON.stringify({
      token: "google_oauth_" + Math.random().toString(36).substring(7),
      timestamp: Date.now(),
      intent: forBookingForm ? "booking_login" : "login",
      forBookingForm: forBookingForm,
      spaceId: spaceId,
      returnUrl:
        returnUrl || (forBookingForm ? window.location.pathname : null),
    });

    sessionStorage.setItem("google_oauth_state", state);

    const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    authUrl.searchParams.append("client_id", clientId);
    authUrl.searchParams.append("redirect_uri", redirectUri);
    authUrl.searchParams.append("response_type", "code");
    authUrl.searchParams.append("scope", "email profile openid");
    authUrl.searchParams.append("access_type", "online");
    authUrl.searchParams.append("prompt", "select_account");
    authUrl.searchParams.append("state", state);

    window.location.href = authUrl.toString();
  };

  // Handle return after successful login
  useEffect(() => {
    if (rt || rtp || targetUid) {
      sessionStorage.setItem("loginReturnRT", rt || "");
      sessionStorage.setItem("loginReturnRTP", rtp || "");
      sessionStorage.setItem("loginReturnUID", targetUid || "");
    }
  }, [rt, rtp, targetUid]);

  // Handle auto-redirect after login (only for non-booking form cases)
  useEffect(() => {
    if (
      isAuthenticated &&
      !targetUid &&
      (window.location.pathname === "/" ||
        window.location.pathname === "/login")
    ) {
      // Wait a moment for Redux state to update before redirecting
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 100);
    }
  }, [isAuthenticated, targetUid, navigate]);

  const resetMessages = useCallback(() => {
    if (error) dispatch(clearError());
  }, [dispatch, error]);

  const handleClose = useCallback(() => {
    sessionStorage.removeItem("loginReturnRT");
    sessionStorage.removeItem("loginReturnRTP");
    sessionStorage.removeItem("loginReturnUID");
    sessionStorage.removeItem("booking_form_context");

    if (isInModal) {
      closeModal(500, resetMessages);
    } else if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  }, [isInModal, closeModal, resetMessages, onClose, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const showErrorModal = (message) => {
    if (isInModal) {
      openModal({
        content: <p>{message}</p>,
        type: "error",
        autoClose: true,
        closeDelay: 3000,
      });
    } else {
      alert(message);
    }
  };

  /**
   * Handles successful login. For booking forms, the pending data
   * is NOT cleared here - it's handled by the CompactBookingForm
   * after the booking is successfully created. This ensures the
   * loading state remains visible.
   */
  const handleLoginSuccess = (
    action,
    isImpersonation = false,
    isGoogleLogin = false,
  ) => {
    const successMsg =
      action.payload?.message ||
      (isImpersonation
        ? t(
            "modal.login.impersonationSuccess",
            "Successfully logged in as user",
          )
        : t("modal.login.success", "Login successful"));

    const returnRT = sessionStorage.getItem("loginReturnRT");
    const returnRTP = sessionStorage.getItem("loginReturnRTP");
    const bookingContext = sessionStorage.getItem("booking_form_context");

    sessionStorage.removeItem("loginReturnRT");
    sessionStorage.removeItem("loginReturnRTP");
    sessionStorage.removeItem("loginReturnUID");

    // ✅ ADD THIS CHECK - If we're on payment result page, stay there
    const currentPath = window.location.pathname;
    if (currentPath.includes("/payment-result")) {
      console.log("[Login] On payment-result page, staying here after login");
      if (isInModal) {
        closeModal();
      }
      // Just close modal and stay on the same page
      return;
    }
    // ============================================================
    // NEW: Opt-in delegation for non-booking-form callers.
    // Only fires when the caller explicitly opts in via
    // delegateRedirectToCaller. Existing callers (CompactBookingForm,
    // SignupForm, etc.) don't set this prop, so they hit the original
    // code paths below unchanged.
    // ============================================================
    if (
      delegateRedirectToCaller &&
      onSuccess &&
      !forBookingForm &&
      !bookingContext &&
      !targetUid
    ) {
      console.log(
        "[Login] delegateRedirectToCaller: deferring to caller onSuccess",
      );
      if (isInModal) {
        closeModal();
      }
      onSuccess();
      return;
    }

    // Handle booking form login
    if (forBookingForm || bookingContext) {
      let context = null;
      if (bookingContext) {
        try {
          context = JSON.parse(bookingContext);
          sessionStorage.removeItem("booking_form_context");
        } catch (e) {
          console.error("Failed to parse booking context:", e);
        }
      }

      // IMPORTANT: DO NOT clear pending data here
      // The CompactBookingForm handles this after the booking is created
      // This preserves the loading state during the booking process

      if (isInModal) {
        closeModal();
      }

      if (onSuccess) {
        console.log("Calling onSuccess callback for booking form");
        onSuccess();
        return;
      }

      if (context && context.returnUrl) {
        console.log("Redirecting to returnUrl:", context.returnUrl);
        navigate(context.returnUrl, { replace: true });
        return;
      }

      if (spaceId) {
        console.log("Redirecting to space:", spaceId);
        navigate(`/space/${spaceId}`, { replace: true });
        return;
      }

      console.log("No callback or returnUrl, just closing modal");
      return;
    }

    // Regular login (non-booking form)
    if (isInModal) {
      closeModal();
    }

    // Handle admin preview flow (only for non-booking forms)
    if (returnRT === "app.space" && returnRTP) {
      try {
        const returnParams = JSON.parse(decodeURIComponent(returnRTP));
        if (returnParams.spaceID) {
          navigate(`/space/${returnParams.spaceID}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error("[Login] Failed to parse return params:", error);
      }
    }

    // ONLY redirect to dashboard for regular login (not booking form)
    // Wait a moment for Redux state to update before redirecting
    setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoginInProgress(true);

    // Track login attempt - validation-formulaire-connexion event (ALWAYS fires)

    try {
      const action = await dispatch(
        login({
          credentials: {
            email: credentials.email,
            password: credentials.password,
          },
          language: language.code,
          targetUid: targetUid || null,
          forBookingForm: forBookingForm,
          spaceId: spaceId,
        }),
      );

      if (login.fulfilled.match(action)) {
        // Track successful login
        trackAuthEvent("validation-formulaire-connexion", {
          method: "email",
          user_type: targetUid ? "admin_impersonation" : "regular",
          email: credentials.email,
          for_booking_form: forBookingForm,
          status: "success",
        });

        handleLoginSuccess(action, !!targetUid, false);
      } else if (login.rejected.match(action)) {
        // Track failed login
        
        showErrorModal(
          action.payload?.[0]?.message ||
            t("modal.login.loginFailed", "Login failed. Please try again."),
        );
      }
    } catch (error) {
      // Track error login
      trackAuthEvent("validation-formulaire-connexion", {
        method: "email",
        user_type: targetUid ? "admin_impersonation" : "regular",
        email: credentials.email,
        for_booking_form: forBookingForm,
        status: "error",
        reason: error.message,
      });
      showErrorModal(
        t(
          "modal.login.unexpectedError",
          "An unexpected error occurred. Please try again.",
        ),
      );
    } finally {
      setLoginInProgress(false);
    }
  };

  const handleForgotPassword = () => {
    if (isInModal) {
      openModal({
        content: <ForgotPasswordForm />,
        title: t("modal.forgotpwd.title"),
      });
    } else {
      navigate("/forgot-password");
    }
  };

  const handleRegister = (type) => {
    if (isInModal) {
      openModal({
        content: (
          <SignupForm
            type={type}
            forBookingForm={forBookingForm}
            spaceId={spaceId}
            returnUrl={returnUrl}
            onSuccess={() => {
              if (forBookingForm && onSuccess) {
                onSuccess();
              }
            }}
          />
        ),
        title: t(
          `modal.register.title${type === "provider" ? "Provider" : "Seeker"}`,
        ),
      });
    } else {
      navigate(`/register/${type}`);
    }
  };

  useEffect(() => {
    return () => {
      resetMessages();
    };
  }, [resetMessages]);

  return (
    <div className={styles.loginModalContainer}>
      {targetUid && (
        <div className={styles.adminModeBanner}>
          <div className={styles.adminModeWarning}>
            <div className={styles.adminModeIcon}>🔐</div>
            <div className={styles.adminModeContent}>
              <h4>{t("modal.login.adminAccessMode", "Admin Access Mode")}</h4>
              {isAdminPreviewFlow ? (
                <>
                  <p>
                    {t(
                      "modal.login.previewSpaceAsAdmin",
                      "You are previewing a space as an admin.",
                    )}
                  </p>
                  <p>
                    {t(
                      "modal.login.enterAdminCredentials",
                      "Enter <strong>admin credentials</strong> to continue:",
                    )}
                  </p>
                  <p className={styles.targetUid}>
                    {t("modal.login.targetUserId", "Target User ID")}:{" "}
                    <code>{targetUid}</code>
                  </p>
                </>
              ) : (
                <>
                  <p>
                    {t(
                      "modal.login.enterAdminCredentialsToLogin",
                      "Enter <strong>admin credentials</strong> to login as user:",
                    )}
                  </p>
                  <p className={styles.targetUid}>
                    {t("modal.login.userId", "User ID")}:{" "}
                    <code>{targetUid}</code>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Form Header Text */}
      {forBookingForm && !targetUid && (
        <div className={styles.bookingLoginHeader}>
          <p className={styles.bookingLoginText}>
            {t(
              "modal.login.bookingLoginText",
              "Please log in or create an account to continue your booking. Your data is protected.",
            )}
          </p>
        </div>
      )}

      {/* Google Sign In Section */}
      {!targetUid && !loginInProgress && (
        <>
          {forBookingForm && (
            <div className={styles.googleLoginLabel}>
              {t("modal.login.withMyGoogleAccount", "With my Google account")}
            </div>
          )}
          <div className={styles.googleLoginContainer}>
            <button
              onClick={handleGoogleLoginClick}
              className={styles.googleLoginButton}
              disabled={loginInProgress}
              data-testid="google-login-button"
            >
              <svg
                width="18"
                height="18"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
              >
                <path
                  fill="#FFC107"
                  d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                />
                <path
                  fill="#FF3D00"
                  d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                />
                <path
                  fill="#4CAF50"
                  d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                />
                <path
                  fill="#1976D2"
                  d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                />
              </svg>
              <span>
                {t("modal.login.signInWithGoogle", "Sign in with Google")}
              </span>
            </button>
          </div>
        </>
      )}

      {!targetUid && loginInProgress && (
        <div className={styles.googleLoginLoading}>
          <div className={styles.loader}></div>
          <p>{t("modal.login.processing", "Processing...")}</p>
        </div>
      )}

      {!targetUid && !loginInProgress && (
        <div className={styles.loginModuleSeparationBox}>
          <hr />
          <p>{t("modal.login.or")}</p>
          <hr />
        </div>
      )}

      {/* Email Login Section */}
      {!targetUid && !loginInProgress && forBookingForm && (
        <div className={styles.emailLoginLabel}>
          {t("modal.login.withEmailAddress", "With an email address")}
        </div>
      )}

      <form className={styles.formOnSubmit} onSubmit={handleSubmit}>
        <InputWithIcon
          icon={emailIcon}
          type="email"
          name="email"
          placeholder={
            targetUid
              ? t("modal.login.adminEmail", "Admin Email")
              : t("modal.login.email")
          }
          handleChange={handleChange}
          value={credentials.email}
          disabled={loginInProgress}
          required
        />
        <InputWithIcon
          icon={passwordIcon}
          type="password"
          name="password"
          placeholder={
            targetUid
              ? t("modal.login.adminPassword", "Admin Password")
              : t("modal.login.password")
          }
          handleChange={handleChange}
          value={credentials.password}
          disabled={loginInProgress}
          required
        />

        {/* ✅ MOVED BUTTON INTO ITS OWN CONTAINER */}
        <div className={styles.buttonWrapper}>
          <button
            type="submit"
            className={`${styles.loginModuleBtnLogin} ${styles.loginModuleBtn}`}
            disabled={loginInProgress}
          >
            {targetUid
              ? t("modal.login.continueAsAdmin", "Continue as Admin")
              : t("modal.login.login", "Se connecter")}
          </button>
        </div>
      </form>
      {!targetUid && (
        <a
          className={styles.loginModuleForgotPassword}
          onClick={handleForgotPassword}
        >
          {t("modal.login.forgotenPassword")}
        </a>
      )}
      {error && Array.isArray(error) && error.length > 0 && (
        <div aria-live="polite" className={styles.errorMessage}>
          {error.map((err, index) => (
            <p key={index}>{err.message}</p>
          ))}
        </div>
      )}

      {!targetUid && !loginInProgress && (
        <>
          {forBookingForm && (
            <p className={styles.loginModuleBoldTxt}>
              {t("modal.login.notRegistered")}
            </p>
          )}

          {forBookingForm && (
            <button
              className={`${styles.loginModuleBtnBuyer} ${styles.loginModuleBtn}`}
              onClick={() => handleRegister("seeker")}
              disabled={loginInProgress}
            >
              {forBookingForm
                ? t("modal.login.registerSeekerForBookingForm")
                : t("modal.login.registerSeeker")}
            </button>
          )}
        </>
      )}

      {targetUid && (
        <button
          className={styles.cancelImpersonationBtn}
          onClick={handleClose}
          disabled={loginInProgress}
        >
          {t("modal.login.cancel", "Cancel")}
        </button>
      )}
    </div>
  );
};

export default Login;
